import { Info, Upload } from 'lucide-react'
import { ReactNode } from 'react'
import Dropzone from 'react-dropzone'
import { Control, FieldArrayPath, FieldValues, Path } from 'react-hook-form'
import { toast } from 'sonner'

import { cn } from '@/lib/utils'
import { addPrefixToObject, removePrefix } from '@/utils/prefixer'

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '../../ui/form'
import { Text } from '../data-display/text'
import InputImageItem from './input-image.item'
import { Label } from './label'

// prettier-ignore
export interface InputImageProps<
  TFieldValues extends FieldValues                  = FieldValues,
  TName        extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
> extends
  addPrefixToObject<React.HTMLAttributes<HTMLDivElement>,        'wrapper_'>,
  addPrefixToObject<React.InputHTMLAttributes<HTMLInputElement>, 'input_'>
{
  field_control:       Control<TFieldValues>
  field_name:          TName
  field_label?:        ReactNode
  field_description?:  ReactNode
  field_files:         File[],
  field_append:        (files: File[]) => void
  field_replace:       (files: File[]) => void
  field_remove:        (index: number) => void
  field_maxFiles:      number
  field_maxFileSizeMb: number
}

const Component = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
>({
  field_control,
  field_name,
  field_label,
  field_description,
  field_files,
  field_append,
  field_replace,
  field_remove,
  field_maxFiles,
  field_maxFileSizeMb,
  ...otherProps
}: InputImageProps<TFieldValues, TName>) => {
  // prettier-ignore
  const prefixedProps = {
    input: removePrefix<React.InputHTMLAttributes<HTMLInputElement>>(otherProps, 'input_'),
  }

  return (
    <FormField
      control={field_control}
      name={field_name as Path<TFieldValues>}
      render={() => (
        <FormItem>
          {field_label && <Label>{field_label}</Label>}
          <FormControl>
            <Dropzone
              onDropAccepted={(acceptedFiles) => {
                if (field_maxFiles > 1) {
                  const nAbleToAdd = Math.max(
                    field_maxFiles - field_files.length,
                    0,
                  )
                  if (nAbleToAdd !== acceptedFiles.length) {
                    const errorMessage = `Limite de ${field_maxFiles} arquivo(s) alcançado`
                    toast.error(errorMessage)
                  }
                  field_append(acceptedFiles.slice(0, nAbleToAdd))
                } else {
                  field_replace(acceptedFiles.slice(0, 1))
                }
              }}
              onDropRejected={(rejectedFiles) => {
                rejectedFiles.forEach((file) =>
                  file.errors.forEach((error) => {
                    if (error.code === 'file-too-large')
                      toast.error(`Arquivo grande demais - ${file.file.name}`)
                  }),
                )
              }}
              accept={{
                'image/png': ['.png'],
                'image/jpg': ['.jpg'],
                'image/jpeg': ['.jpeg'],
              }}
              maxSize={field_maxFileSizeMb * 1000 * 1000}
              multiple={field_maxFiles > 1}
            >
              {({
                getRootProps,
                getInputProps,
                isDragAccept,
                isDragReject,
              }) => (
                <section className="space-y-4">
                  <div
                    {...getRootProps()}
                    data-is-valid={isDragAccept}
                    data-is-invalid={isDragReject}
                    data-disabled={prefixedProps.input.disabled}
                    className={cn(
                      'bg-background p-2 shadow-sm',
                      'rounded-lg border border-dashed border-muted-foreground/20',
                      'cursor-pointer hover:border-muted-foreground/50',
                      'data-[is-valid=true]:border-lime-500',
                      'data-[is-invalid=true]:border-red-500',
                      'data[disabled=true]:cursor-not-allowed data[disabled=true]:opacity-50',
                    )}
                  >
                    <input {...getInputProps()} />

                    <div className="flex flex-col items-center gap-4">
                      <Upload
                        className="size-8 text-muted-foreground"
                        strokeWidth={0.5}
                      />

                      <div className="space-y-2 text-center text-xs">
                        <p>Arraste e solte ou clique para enviar arquivo(s)</p>
                        <div className="flex items-center justify-center gap-2">
                          <Info className="size-3" strokeWidth={1} />
                          <Text variant="soft" className="text-xs">
                            Máximo de {field_maxFileSizeMb} Mb
                          </Text>
                        </div>
                      </div>
                    </div>
                  </div>

                  {field_files.length > 0 && (
                    <div className="max-h-80 space-y-2 overflow-auto">
                      {field_files.map((file, index) => (
                        <InputImageItem
                          key={file.name}
                          file={file}
                          index={index}
                          remove={field_remove}
                        />
                      ))}
                    </div>
                  )}

                  <Text variant="soft" className="text-right text-xs">
                    Máximo de {field_maxFiles} arquivo(s)
                  </Text>
                </section>
              )}
            </Dropzone>
          </FormControl>

          {field_description && (
            <FormDescription>{field_description}</FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
Component.displayName = 'InputImage'

export { Component as InputImage }
